<script lang="ts" setup>
import { NumericEvaluationParameterDto } from '@/api/generator';
import PtwDeleteButton from '@/components/buttons/PtwDeleteButton.vue';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import VeeCheckbox from '@/components/forms/Checkbox/VeeCheckbox.vue';
import VeeTextarea from '@/components/forms/Textarea/VeeTextarea.vue';
import { useFormMode } from '@/components/forms/useFormMode';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils';
import { edit, error, warning } from '@/components/icons/icons';
import ShowMore from '@/components/ShowMore/ShowMore.vue';
import { useProtocolsStore } from '@/stores/protocolsStore';
import { useUnitsStore } from '@/stores/unitsStore';
import { translateEntityProperty } from '@/utils/helper';
import { I18nKey } from '@/utils/typings';
import { mdiDragHorizontal } from '@mdi/js';
import { isNil, mapValues, values } from 'lodash';
import { useField } from 'vee-validate';
import { computed, onMounted, toRef, watch } from 'vue';
import LimitField from './LimitField.vue';
import EnumOrQuantityLabel from './EnumOrQuantityLabel.vue';
import ParameterSetTable from './ParameterSetTable.vue';

interface EvaluationsManualTasksEnumTileProps {
  name: string; // evaluationScopes[{tableIndex = 0}].evaluationParameters[{index}]
  showInBeamConstancyCheck?: boolean;
}

const emits = defineEmits<{
  edit: [param: NumericEvaluationParameterDto];
  remove: [param: NumericEvaluationParameterDto];
  select: [];
}>();

const props = defineProps<EvaluationsManualTasksEnumTileProps>();

const mode = useFormMode();

const unitsStore = useUnitsStore();

const protocolsStore = useProtocolsStore();

onMounted(async () => {
  await unitsStore.initialize();
  await protocolsStore.initialize();
});

const { value: tileData } = useField<NumericEvaluationParameterDto>(toRef(() => props.name));

function handleRemove() {
  emits('remove', tileData.value);
}

function handleEdit() {
  emits('edit', tileData.value);
}

const checkBoxValuePath = computed(() => toRef(() => `${props.name}.isEnabled`));

const limits = {
  lowerFailed: 'lowerFailedLimit',
  lowerWarning: 'lowerWarningLimit',
  upperWarning: 'upperWarningLimit',
  upperFailed: 'upperFailedLimit',
};

const limitFields = mapValues(limits, (limit) =>
  useField<number | undefined | null>(toRef(() => `${props.name}.${limit}`)),
);

const rangeDisabled = computed(
  () => mode.value === 'details' || tileData.value.limitType === 'WithoutEvaluation',
);

function removeLimitValues() {
  values(limitFields).forEach((limit) => limit.setValue(null));
}

watch(
  () => tileData.value.limitType,
  (value) => {
    if (value === 'WithoutEvaluation') {
      removeLimitValues();
    }
  },
);

const inputRangeDescription = computed(() => {
  const hasRange = !(isNil(tileData.value.min) && isNil(tileData.value.max));

  const min = tileData.value.min ?? '';
  const max = tileData.value.max ?? '';
  const unit = tileData.value.limitUnitAbbreviation ?? '';

  return hasRange ? `(${min}...${max}) ${unit}` : undefined;
});

// Force ShowMore to collapse when definition changes
const showMoreKey = computed(() => tileData.value.definition?.id ?? 'no-definition');
</script>

<template>
  <div class="tile elevation-2" data-testid="EvaluationQuantityTile">
    <div v-if="mode !== 'details' && !showInBeamConstancyCheck" class="stripe left-stripe">
      <v-icon :icon="mdiDragHorizontal" class="ml-2 mt-3 cursor-move handle" />
    </div>
    <div v-if="showInBeamConstancyCheck" class="stripe left-stripe">
      <VeeCheckbox
        :name="checkBoxValuePath.value"
        :placeholder="$t('Required')"
        :mode="'edit'"
        :disabled="mode === 'details'"
        class="ml-2"
        @change="emits('select')"
        @click.stop
      />
    </div>

    <div class="w-100 pa-5">
      <v-row>
        <v-col cols="3">
          <EnumOrQuantityLabel :data="tileData" :label="$t('Quantity_one')" />
        </v-col>

        <v-col cols="3">
          <FormLabel v-if="tileData.evaluationType" :label="$t('EvaluationType_one')">
            {{
              tileData.evaluationUnitAbbreviation
                ? $t(`EvaluationTypeEnum.${tileData.evaluationType}` as I18nKey)
                : $t(`EvaluationTypeEnum.${tileData.evaluationType}` as I18nKey)
            }}
          </FormLabel>

          <FormLabel v-else :label="$t('EvaluationType_one')"> {{ EMPTY_FIELD_SIGN }}</FormLabel>
        </v-col>
        <v-col cols="3">
          <FormLabel v-if="tileData.evaluationType" :label="$t('EvaluationUnit_one')">
            {{ tileData.evaluationUnitAbbreviation || EMPTY_FIELD_SIGN }}</FormLabel
          >
        </v-col>
      </v-row>

      <ShowMore
        :key="showMoreKey"
        :collapsedHeight="0"
        class="my-3"
        :moreLabel="$t('ShowMoreDetails')"
        :lessLabel="$t('ShowLessDetails')"
        color="white"
        buttonPosition="top"
      >
        <v-row class="py-3">
          <v-col cols="3">
            <FormLabel :label="$t('Definition_one')">
              {{ translateEntityProperty('name', tileData.definition) }}
            </FormLabel>
          </v-col>

          <v-col cols="3">
            <FormLabel :label="$t('DefinitionUnit_one')">
              {{
                unitsStore.getById(tileData.definition?.unitId)?.abbreviation || EMPTY_FIELD_SIGN
              }}
            </FormLabel>
          </v-col>

          <v-col cols="3">
            <FormLabel :label="$t('ValueType')">
              {{
                tileData.definition?.valueType
                  ? $t(tileData.definition.valueType as I18nKey)
                  : EMPTY_FIELD_SIGN
              }}
            </FormLabel>
          </v-col>
          <v-col cols="3">
            <FormLabel :label="$t('DefinitionProtocol_one')">
              {{
                protocolsStore.getById(tileData.definition?.protocolId)?.name || EMPTY_FIELD_SIGN
              }}
            </FormLabel>
          </v-col>
        </v-row>

        <v-row v-if="tileData.definitionParameterValueSet?.values">
          <v-col cols="12">
            <h6 class="v-label v-label-overwrite mb-1 d-block text-wrap">
              {{ $t('DefinitionParameterSet_one') }}
            </h6>
            <ParameterSetTable
              :selectedDefinition="tileData.definition"
              :displayedValueSet="tileData.definitionParameterValueSet"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="3">
            <FormLabel v-if="tileData.evaluationType" :label="$t('InputRangeForExecution')">
              <template v-if="inputRangeDescription">
                {{ inputRangeDescription }}
              </template>
              <template v-else>
                {{ EMPTY_FIELD_SIGN }}
              </template>
            </FormLabel>
          </v-col>
          <v-col cols="3">
            <FormLabel
              v-if="tileData.evaluationType && tileData.definition?.valueType !== 'IntegerNumber'"
              :label="$t('NumberOfDisplayedDecimals')"
            >
              {{ tileData.decimalPlaces }}
            </FormLabel>
          </v-col>
        </v-row>
      </ShowMore>
      <VDivider class="v-theme--ptwLightTheme text-primary-lighten-3 my-8" />

      <template v-if="tileData?.limitType !== 'WithoutEvaluation'">
        <v-row class="d-flex align-start">
          <v-col cols="3">
            <FormLabel v-if="tileData.evaluationType" :label="$t('LimitTypeLabel')">
              {{ $t(`LimitType.${tileData.limitType}` as I18nKey) || EMPTY_FIELD_SIGN }}</FormLabel
            >
          </v-col>
          <v-col cols="9">
            <v-row>
              <v-col cols="3">
                <!-- one -->
                <LimitField
                  :name="`${name}.lowerFailedLimit`"
                  :label="$t('LowerFailedLimit')"
                  :mode="mode"
                  :disabled="rangeDisabled"
                  :icon="error"
                  :suffix="tileData?.evaluationUnitAbbreviation"
                  :placeholder="showInBeamConstancyCheck ? $t('Required') : ''"
                  :numberOfDisplayedDecimals="tileData.decimalPlaces"
                  :valueType="tileData.definition?.valueType"
                  :evaluationType="tileData.evaluationType"
                  iconColor="RED"
                />
              </v-col>

              <v-col cols="3">
                <!-- two -->

                <LimitField
                  :name="`${name}.lowerWarningLimit`"
                  :label="$t('LowerWarningLimit')"
                  :mode="mode"
                  :disabled="rangeDisabled"
                  :icon="warning"
                  :suffix="tileData?.evaluationUnitAbbreviation"
                  :numberOfDisplayedDecimals="tileData.decimalPlaces"
                  :valueType="tileData.definition?.valueType"
                  :evaluationType="tileData.evaluationType"
                  iconColor="YELLOW"
                />
              </v-col>

              <v-col cols="3">
                <!-- three -->

                <LimitField
                  :name="`${name}.upperWarningLimit`"
                  :label="$t('UpperWarningLimit')"
                  :mode="mode"
                  :disabled="rangeDisabled"
                  :icon="warning"
                  :suffix="tileData?.evaluationUnitAbbreviation"
                  :numberOfDisplayedDecimals="tileData.decimalPlaces"
                  :valueType="tileData.definition?.valueType"
                  :evaluationType="tileData.evaluationType"
                  iconColor="YELLOW"
                />
              </v-col>

              <v-col cols="3">
                <!-- four -->
                <LimitField
                  :name="`${name}.upperFailedLimit`"
                  :label="$t('UpperFailedLimit')"
                  :mode="mode"
                  :disabled="rangeDisabled"
                  :icon="error"
                  :suffix="tileData?.evaluationUnitAbbreviation"
                  :placeholder="showInBeamConstancyCheck ? $t('Required') : ''"
                  :numberOfDisplayedDecimals="tileData.decimalPlaces"
                  :valueType="tileData.definition?.valueType"
                  :evaluationType="tileData.evaluationType"
                  iconColor="RED"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </template>

      <v-row>
        <v-col v-if="!showInBeamConstancyCheck" cols="6">
          <VeeTextarea :label="$t('Instruction_one')" :name="`${name}.instruction`" :mode="mode" />
        </v-col>
      </v-row>
    </div>
    <div v-if="mode !== 'details' && !showInBeamConstancyCheck" class="stripe right-stripe">
      <TextVBtn size="small" :icon="edit" class="mt-0" color="secondary" @click="handleEdit" />
      <PtwDeleteButton @delete="handleRemove" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.expand-button-row {
  width: 100%;
  display: flex;
  justify-content: left;
  font-weight: bold;
  align-items: center;
  cursor: pointer;
  p {
    width: 180px;
  }
}
.tile {
  border: 0px solid rgb(var(--v-theme-primary-lighten-4));
  border-radius: 4px !important;
  background: white;
  position: relative;
  display: flex;
}

.separator {
  margin: 32px 0;
  border-top: 1px solid rgb(var(--v-theme-primary-lighten-4));
  width: 100%;
}

.stripe {
  width: 48px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  i {
    margin: 0 !important;
  }
}

.left-stripe {
  border-right: 1px solid rgb(var(--v-theme-primary-lighten-4));
}

.right-stripe {
  border-left: 1px solid rgb(var(--v-theme-primary-lighten-4));
}
</style>
